import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, PackageCheck, PackagePlus } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import StatusChip from '../../components/ui/StatusChip';
import Modal from '../../components/ui/Modal';
import RackSelector from '../../components/ui/RackSelector';
import WorkflowTimeline from '../../components/ui/WorkflowTimeline';

const InwardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [receipt, setReceipt] = useState(null);
  const [grn, setGrn] = useState(null);
  const [quarantinePending, setQuarantinePending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [putAwayOpen, setPutAwayOpen] = useState(false);
  const [placements, setPlacements] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [repackForm, setRepackForm] = useState({ required: false, notes: '' });
  const [repackSubmitting, setRepackSubmitting] = useState(false);

  const loadReceipt = () => api.get(`/inward/${id}`)
    .then((r) => {
      setReceipt(r.data.data);
      setRepackForm({
        required: !!r.data.data?.repack?.required,
        notes: r.data.data?.repack?.notes || '',
      });
    })
    .catch(console.error)
    .finally(() => setLoading(false));

  const loadWorkflowContext = () => {
    api.get(`/grn?inwardReceipt=${id}`).then((r) => setGrn(r.data.data?.[0] || null)).catch(() => {});
    api.get(`/quarantine?inwardReceipt=${id}&status=quarantined`).then((r) => setQuarantinePending(r.data.count || 0)).catch(() => {});
  };

  useEffect(() => {
    loadReceipt();
    loadWorkflowContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const workflowSteps = receipt ? (() => {
    const stage = (done, current) => (done ? 'done' : current ? 'current' : 'pending');
    const grnDone = !!grn && grn.status === 'generated';
    const qaDone = grnDone && grn.qaStatus !== 'pending';
    const qaRejected = grnDone && grn.qaStatus === 'rejected';
    const putawayDone = receipt.status === 'stored';
    return [
      { label: 'Security Inward Created', state: 'done' },
      { label: 'GRN Created', state: stage(grnDone, !grnDone) },
      {
        label: qaRejected ? 'Quality Assurance — Rejected' : 'Quality Assurance',
        state: qaRejected ? 'rejected' : stage(qaDone, grnDone && !qaDone),
      },
      {
        label: quarantinePending > 0 ? 'Quarantine Review Pending' : 'Putaway Completed',
        state: quarantinePending > 0 ? 'current' : stage(putawayDone, qaDone && !putawayDone),
      },
      { label: 'Material Available in Inventory', state: stage(putawayDone, false) },
    ];
  })() : [];

  const openPutAway = () => {
    const items = receipt.items || [];
    const storableItems = items.filter((it) => Number(it.quantity) > 0);
    if (storableItems.length === 0) {
      toast.error('No items with a quantity greater than 0 to put away');
      return;
    }
    if (storableItems.length < items.length) {
      toast(`${items.length - storableItems.length} item(s) with 0 quantity were skipped`, { icon: '⚠️' });
    }
    setPlacements(
      storableItems.map((it) => ({
        itemName: it.itemName,
        quantity: it.quantity,
        unit: it.unit,
        rackId: '',
        floor: '',
      }))
    );
    setPutAwayOpen(true);
  };

  const updatePlacement = (i, val) => {
    setPlacements((prev) => prev.map((p, idx) => (idx === i ? { ...p, ...val } : p)));
  };

  const allPlaced = placements.length > 0 && placements.every((p) => p.rackId && p.floor);

  const handleRepackSave = async () => {
    setRepackSubmitting(true);
    try {
      const res = await api.patch(`/inward/${id}/repack`, {
        required: repackForm.required,
        notes: repackForm.notes,
      });
      setReceipt(res.data.data);
      toast.success('Repack decision saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save repack decision');
    } finally {
      setRepackSubmitting(false);
    }
  };

  const handlePutAway = async () => {
    if (!allPlaced) return toast.error('Assign a rack and floor for every item');
    setSubmitting(true);
    try {
      const res = await api.post(`/inward/${id}/putaway`, { placements });
      setReceipt(res.data.data);
      toast.success('Items put away successfully');
      setPutAwayOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Put-away failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => <div key={i} className="zip-card h-32 skeleton" />)}
    </div>
  );
  if (!receipt) return (
    <div className="text-center text-zip-muted py-24">Receipt not found</div>
  );

  return (
    <div className="space-y-5 max-w-5xl">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/inward')}
          className="flex items-center gap-2 text-sm text-zip-muted hover:text-zip-text transition-colors"
        >
          <ArrowLeft size={15} /> Back to Inward Receipts
        </button>
        {receipt.status === 'received' && can('rack.manage') && (
          <button type="button" onClick={openPutAway} className="btn-primary flex items-center gap-2">
            <PackageCheck size={15} /> Put Away
          </button>
        )}
      </div>

      <WorkflowTimeline steps={workflowSteps} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Items table */}
        <div className="lg:col-span-2 zip-card overflow-hidden">
          <div className="px-5 py-4 border-b border-zip-border">
            <h3 className="font-semibold text-zip-text tracking-tight">Items Received</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-zip-surface border-b border-zip-border">
                  {['Item Name', 'Quantity', 'UOM'].map((h) => (
                    <th key={h} className="zip-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {receipt.items.map((item, i) => (
                  <tr key={i} className="border-b border-zip-border last:border-0">
                    <td className="zip-td font-medium">{item.itemName}</td>
                    <td className="zip-td font-mono">{item.quantity}</td>
                    <td className="zip-td font-mono text-zip-muted">{item.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sidebar info */}
        <div className="space-y-4">
          <div className="zip-card p-5 space-y-3">
            <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">
              Receipt Info
            </h3>
            {[
              ['Inward No.',   <span className="font-mono text-zip-red text-xs">{receipt.inwardNumber}</span>],
              ['Invoice No.',  <span className="font-mono text-xs">{receipt.invoiceNumber}</span>],
              ['Status',       <StatusChip status={receipt.status} />],
              ['Supplier',     receipt.supplier?.name],
              ['Invoice Date', format(new Date(receipt.invoiceDate), 'dd MMM yyyy')],
              ['Total Items',  <span className="font-mono">{receipt.totalItems}</span>],
              ...(receipt.poNumber ? [['PO No.', <span className="font-mono text-xs">{receipt.poNumber}</span>]] : []),
              ...(receipt.challanNumber ? [['Challan No.', <span className="font-mono text-xs">{receipt.challanNumber}</span>]] : []),
              ...(receipt.totalReceiptQuantity ? [['Invoice Qty', <span className="font-mono">{receipt.totalReceiptQuantity}</span>]] : []),
              ...(receipt.arrivalDateTime ? [['Arrival Date & Time', format(new Date(receipt.arrivalDateTime), 'dd MMM yyyy, HH:mm')]] : []),
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-zip-muted">{k}</span>
                <span className="text-zip-text font-medium">{v}</span>
              </div>
            ))}
          </div>

          {(receipt.vehicleNumber || receipt.docketLRNumber || receipt.transporterName || receipt.driverName || receipt.driverPhone) && (
            <div className="zip-card p-5 space-y-3">
              <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">
                Transport Info
              </h3>
              {[
                receipt.vehicleNumber   && ['Vehicle No.',    receipt.vehicleNumber],
                receipt.docketLRNumber  && ['Docket / LR No.', receipt.docketLRNumber],
                receipt.transporterName && ['Transporter',    receipt.transporterName],
                receipt.driverName      && ['Driver Name',    receipt.driverName],
                receipt.driverPhone     && ['Driver Phone',   receipt.driverPhone],
              ].filter(Boolean).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-zip-muted">{k}</span>
                  <span className="text-zip-text font-mono text-xs">{v}</span>
                </div>
              ))}
            </div>
          )}

          <div className="zip-card p-5 space-y-3">
            <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3">
              Audit Info
            </h3>
            {[
              ['Received By', receipt.receivedBy?.name],
              ['Created At',  format(new Date(receipt.createdAt), 'dd MMM yyyy HH:mm')],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-sm">
                <span className="text-zip-muted">{k}</span>
                <span className="text-zip-text font-mono text-xs">{v}</span>
              </div>
            ))}
          </div>

          {/* Repack decision */}
          {can('rack.manage') && (
            <div className="zip-card p-5 space-y-3">
              <h3 className="font-semibold text-zip-text tracking-tight border-b border-zip-border pb-3 flex items-center gap-2">
                <PackagePlus size={15} /> Repack
              </h3>
              {receipt.repack?.decidedAt && (
                <div className="text-xs text-zip-muted">
                  Current decision: <span className="text-zip-text font-medium">
                    {receipt.repack.required ? 'Repack required' : 'Repack not required'}
                  </span> — {format(new Date(receipt.repack.decidedAt), 'dd MMM yyyy HH:mm')}
                  {receipt.repack.notes && <div className="mt-1 text-zip-muted">{receipt.repack.notes}</div>}
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-zip-text cursor-pointer">
                <input
                  type="checkbox"
                  checked={repackForm.required}
                  onChange={(e) => setRepackForm({ ...repackForm, required: e.target.checked })}
                  className="w-4 h-4 accent-zip-red"
                />
                Repack required?
              </label>
              <textarea
                className="zip-input resize-none"
                rows={2}
                placeholder="Repack notes (optional)…"
                value={repackForm.notes}
                onChange={(e) => setRepackForm({ ...repackForm, notes: e.target.value })}
              />
              <button
                type="button"
                onClick={handleRepackSave}
                disabled={repackSubmitting}
                className="btn-primary w-full"
              >
                {repackSubmitting ? 'Saving…' : 'Save Repack Decision'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Put Away modal */}
      <Modal open={putAwayOpen} onClose={() => setPutAwayOpen(false)} title="Put Away Items" width="max-w-2xl">
        <div className="space-y-6">
          {placements.map((p, i) => (
            <div key={i} className="border border-zip-border rounded-xl p-4 space-y-3 bg-zip-surface">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-zip-text">{p.itemName}</span>
                <span className="font-mono text-xs text-zip-muted">{p.quantity} {p.unit}</span>
              </div>
              <RackSelector
                value={{ rackId: p.rackId, floor: p.floor }}
                onChange={(val) => updatePlacement(i, val)}
              />
            </div>
          ))}
          <div className="flex gap-3">
            <button type="button" onClick={handlePutAway} disabled={submitting || !allPlaced}
              className="btn-primary flex items-center gap-2">
              {submitting ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Placing…</>
              ) : 'Confirm Put Away'}
            </button>
            <button type="button" onClick={() => setPutAwayOpen(false)} className="btn-ghost">Cancel</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InwardDetail;
